import { SpaceDto } from "@braoshzmvavz/picnube-common";
import { Avatar, Modal, Text, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import React, { useState } from "react";
import { HiChevronRight, HiCog, HiPlus, HiUsers } from "react-icons/hi";
import useAppState from "src/services/useGlobalState";
import { Drawer } from "vaul";
import { useAppSelector } from "../../app/hooks";
import { MenuAction, MenuContext } from "../menu/menus";
import SpaceSettingsModal from "../modal/SpaceSettingsModal";
import SpaceUsersModal from "../modal/SpaceUsersModal";
import AddSpaceModal from "../modal/AddSpaceModal";
import useApi from "src/services/useApi";

export default function BottomDrawerSpaceSelector(props: {
  targetElement: React.ReactNode;
  onActionClick: (actionConfig: MenuAction, ctx: MenuContext) => void;
}) {
  const profileState = useAppSelector((state) => state.profile);
  const { changeToSpace } = useAppState();
  const [isOpen, setIsOpen] = useState(false);

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const { updateSpaceAccess } = useApi();

  // Modal states
  const [
    isSettingsModalOpened,
    { open: openSettingsModal, close: closeSettingsModal },
  ] = useDisclosure(false);
  const [isUsersModalOpened, { open: openUsersModal, close: closeUsersModal }] =
    useDisclosure(false);
  const [
    isAddSpaceModalOpened,
    { open: openAddSpaceModal, close: closeAddSpaceModal },
  ] = useDisclosure(false);

  // Modal handlers
  const handleSpaceSettingsSave = (updatedSpace: SpaceDto) => {
    // TODO: Implement space settings update logic
    console.log("Space settings updated:", updatedSpace);
    closeSettingsModal();
  };

  const handleSpaceUsersSave = (addUsers, removeUsers) => {
    updateSpaceAccess(profileState.currentSpace.id, {
      upsertUsers: addUsers,
      removeUsers,
    }).then((response: any) => {
      console.log("updateSpaceAccess response", response);
      closeUsersModal();
    });
  };

  const handleAddSpaceSave = (newSpace: Partial<SpaceDto>) => {
    // TODO: Implement space creation logic
    console.log("New space:", newSpace);
    closeAddSpaceModal();
  };

  const mapSpaceNameForAvatar = (name: string) => {
    return name.replace(/[^a-zA-Z0-9 ]/g, "");
  };

  const renderSpaceButtonNotSelected = (space: SpaceDto) => {
    return (
      <div
        className="flex flex-row justify-between items-center p-2 px-4 gap-2 dark:hover:bg-dark-900 cursor-pointer rounded-lg mx-1"
        onClick={() => {
          setIsOpen(false);
          changeToSpace(space);
        }}
      >
        <div className="flex flex-row justify-between items-center gap-2">
          <Avatar key={space.name} name={mapSpaceNameForAvatar(space.name)} color="initials" />
          <div className="text-md">{space.name}</div>
        </div>

        <HiChevronRight className="text-lg min-w-fit"></HiChevronRight>
      </div>
    );
  };

  const renderOtherSpacesList = () => {
    const otherSpaces = profileState.profile?.spaces
      .filter((space) => space.id !== profileState.currentSpace.id)
      .map((space) => renderSpaceButtonNotSelected(space));

    if (!otherSpaces?.length) {
      return <></>;
    }

    return (
      <>
        {otherSpaces}
        <div className="border-b border-grey-800 w-full h-2" />
      </>
    );
  };

  const renderSpaceIcon = () => {
    if (!profileState.currentSpace) {
      return <div className="flex p-3"></div>;
    }

    const name = profileState.currentSpace.name.replace(/[^a-zA-Z0-9 ]/g, "");
    if (name == null) return null;

    return <Avatar key={profileState.currentSpace.name} name={name} color="initials" />;
  };

  const renderSpaceButton = () => {
    if (!profileState.currentSpace) {
      return <div className="flex p-3"></div>;
    }

    const name = profileState.currentSpace.name;
    if (name == null) return null;

    return (
      <div className="flex flex-row justify-between items-center py-4 px-4 gap-3 mx-1">
        {renderSpaceIcon()}
        <div className="flex flex-col justify-center items-start">
          <div className="text-md font-bold">{name}</div>

          <div className="text-sm text-grey-600">
            {profileState.currentSpace.description}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="w-full flex flex-col">
        <div className="flex flex-col justify-between items-start">
          {renderSpaceButton()}

          <div className="w-full flex flex-col">
            <div
              className="flex flex-row justify-start items-center p-2 px-4 gap-2 dark:hover:bg-dark-900 hover:bg-grey-200 cursor-pointer rounded-lg mx-1"
              onClick={() => {
                setIsOpen(false);
                openSettingsModal();
              }}
            >
              <HiCog></HiCog>
              <div className="text-sm">Settings</div>
            </div>

            <div
              className="flex flex-row justify-start items-center p-2 px-4 gap-2 dark:hover:bg-dark-900 hover:bg-grey-200 cursor-pointer rounded-lg mx-1"
              onClick={() => {
                setIsOpen(false);
                openUsersModal();
              }}
            >
              <HiUsers></HiUsers>
              <div className="text-sm">Manage users</div>
            </div>
          </div>

          <div className="border-b border-grey-800 w-full h-2" />
        </div>
        <div className="flex flex-col py-2">
          {renderOtherSpacesList()}
          <div
            className="flex justify-start items-center gap-2 p-2 px-4 cursor-pointer rounded-lg dark:hover:bg-dark-900 hover:bg-grey-200 dark:active:bg-dark-999 active:bg-grey-200 text-sm mx-1 mt-2"
            onClick={() => {
              setIsOpen(false);
              openAddSpaceModal();
            }}
          >
            <HiPlus></HiPlus>
            Add Space
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Drawer.Root open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
        <Drawer.Trigger>{props.targetElement}</Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/40 bottom-drawer-space-overlay" />
          <Drawer.Content className="dark:bg-grey-900 bg-white flex flex-col rounded-t-[10px] mt-24 h-fit fixed bottom-0 left-0 right-0 outline-none bottom-drawer-space-content z-30">
            <div aria-hidden className="mb-3" />
            <Drawer.Handle />
            <Drawer.Title />
            <Drawer.Description />
            <Drawer.Close />
            <div className="p-2 pt-0">{renderContent()}</div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      <Modal
        opened={isSettingsModalOpened}
        onClose={closeSettingsModal}
        title={
          <div className="flex flex-row gap-2 items-center">
            <HiCog />
            <Text>Space Settings</Text>
          </div>
        }
      >
        <SpaceSettingsModal
          space={profileState.currentSpace}
          onSave={handleSpaceSettingsSave}
        />
      </Modal>

      <Modal
        opened={isUsersModalOpened}
        onClose={closeUsersModal}
        title={
          <div className="flex flex-row gap-2 items-center">
            <HiUsers />
            <Text>Manage Users</Text>
          </div>
        }
        size="lg"
        fullScreen={isMobile}
      >
        <SpaceUsersModal
          space={profileState.currentSpace}
          onSave={handleSpaceUsersSave}
        />
      </Modal>

      <Modal
        opened={isAddSpaceModalOpened}
        onClose={closeAddSpaceModal}
        title={
          <div className="flex flex-row gap-2 items-center">
            <HiPlus />
            <Text>Add Space</Text>
          </div>
        }
      >
        <AddSpaceModal onSave={handleAddSpaceSave} />
      </Modal>
    </>
  );
}
